import { StandardError } from "./StandardError"

const limitForInlinedBinary = 16 * 1024 * 1024

/**
 * Helper class to store and manage Binary data from the database.
 *
 * We handle binaries in one of two ways:
 *  - inline: The binary is stored in the database as a base64 encoded string. This is used for small binaries (less than 16MB).
 *  - reference: The binary is stored in a file storage and a reference to the file is stored in the database. This is used for large binaries (greater than 16MB).
 *
 * OBS!!! At this time we have not implemented the file storage server and therefor reference binaries do not work. Only use inline binaries for now.
 */
export class BinaryRep {
    private inlineBuffer?: string // Base64 encoded buffer since the browser doesn't support Buffers
    toJSON: any

    private constructor(
        private binaryType: "inline" | "reference",
        readonly contentType: string | null,
        readonly fileName: string | null,
    ) {}

    /**
     * Used in the browser to convert a BinaryRep of an image to an image link that can be displayed.
     *
     * @returns - An image link that can be used in the browser to display the image.
     */
    public getImageLink(): string {
        /*        if (this.inlineBuffer === undefined) {
            throw new StandardError("BinaryRep is not inlined")
        }*/

        // It's a bit hard to throw an exception here since it stops the entire page from rendering so we try this
        if (this.inlineBuffer === undefined) return "missingImage"

        return `data:${this.contentType};base64,${this.inlineBuffer}`
    }

    /**
     * This builder function is used in the browser to convert a File object to a BinaryRep object.
     *
     * @param file - The file to convert to a BinaryRep. This is a File object from the browser.
     * @returns - A BinaryRep object that contains the file data as a base64 encoded string.
     */
    static async fromBlob(file: File): Promise<BinaryRep> {
        let binaryType: "inline" | "reference" = "inline"

        // This is a bit large for inlining but we don't allow references yet and we want to store
        // images in the database for now. nginx has it's max body set to 24MB and GRPC as well to allow
        // the upload of a 16MB file. Once these are supported as references we can decrease this limit
        // in nginx and GRPC.
        if (file.size >= limitForInlinedBinary) {
            binaryType = "reference"
            // We should store this in file storage
            throw new StandardError("Can't upload files larger than 16MB currently")
        }

        const reference = new BinaryRep(binaryType, file.type, file.name)
        reference.inlineBuffer = await BinaryRep.blobToBase64(file)

        return reference
    }

    /**
     * Helper function that collects a blob into a base64 encoded string.
     *
     * @param blob - The blob to convert to a base64 encoded string. This is a Blob object from the browser.
     * @returns - A base64 encoded string that contains the blob data.
     */
    private static async blobToBase64(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => {
                resolve((reader.result as string).split(",")[1])
            }

            reader.onerror = reject

            reader.readAsDataURL(blob)
        })
    }

    // Will use context.storage to actually store the binary if necessary
    /**
     * Takes a buffer and converts it to a BinaryRep object.
     *
     * OBS!!! Since we have not yet implemented the file storage server all converted BinaryRep objects will use the inline type even if they should be stored on file.
     *
     * @param buffer - The buffer to convert to a BinaryRep. This is a Buffer object from the server.
     * @param contentType - Content type of the buffer file.
     * @param fileName - File name of the buffer file.
     * @returns - New BinaryRep object that represent the buffer.
     */
    static async fromBuffer(
        buffer: Buffer,
        contentType: string | null = null,
        fileName: string | null = null,
    ): Promise<BinaryRep> {
        let binaryType: "inline" | "reference" = "inline"

        if (buffer.length > limitForInlinedBinary) {
            binaryType = "reference"
            // We should store this in file storage
            throw new StandardError("Not implemented")
        }

        const reference = new BinaryRep(binaryType, contentType, fileName)
        reference.inlineBuffer = buffer.toString("base64")

        return reference
    }

    /**
     * Used in node to convert a BinaryRep object to a Buffer object.
     *
     * @returns - A buffer of the binary data this BinaryRep object represents.
     */
    async getBuffer(): Promise<Buffer> {
        if (this.inlineBuffer === undefined) {
            // It's a bit hard to throw an exception here since it stops the entire page from rendering so we try this
            return Buffer.from("")
            //throw new StandardError("BinaryRep is not inlined")
        }

        return Buffer.from(this.inlineBuffer, "base64")
    }

    
    /**
     * Converts the BinaryRep object to a JSON string that can be stored in the database.
     *
     * @returns - JSON string representation of BinaryRep.
     */
    toString(): string {
        return JSON.stringify({
            binaryType: this.binaryType,
            contentType: this.contentType,
            fileName: this.fileName,
            inlineBuffer: this.inlineBuffer,
        })
    }

    /**
     * In the database the BinaryRep is stored as a JSON string. This function takes a JSON string and converts it to a BinaryRep object that can be used.
     * Will throw an error if the JSON string is not a valid BinaryRep object.
     *
     * @param json - JSON string representation of BinaryRep.
     * @returns New BinaryRep object.
     */
    static fromJSON(json: string): BinaryRep {
        const binaryRep = BinaryRep.fromJSONOrNull(json)

        if (binaryRep === null) {
            throw new StandardError("JSON string is not a BinaryRep")
        }
        return binaryRep
    }

    /**
     * In the database the BinaryRep is stored as a JSON string. This function takes a JSON string and converts it to a BinaryRep object that can be used.
     * Will return null if the JSON string is not a valid BinaryRep object.
     *
     * @param json - JSON string representation of BinaryRep.
     * @returns New BinaryRep object or null if json is invalid.
     */
    static fromJSONOrNull(json: string): BinaryRep | null {
        const obj = JSON.parse(json)

        if (!BinaryRep.isBinary(obj)) return null

        const binaryRep = new BinaryRep(obj.binaryType, obj.contentType, obj.fileName)
        binaryRep.inlineBuffer = obj.inlineBuffer
        return binaryRep
    }

    /**
     * Creates an empty BinaryRep object that can be used to store a binary file.
     * Useful when creating a placeholder value
     *
     * @returns - New BinaryRep object that is empty and can be used to store a binary file.
     */
    static createEmpty(): BinaryRep {
        return new BinaryRep("inline", null, null)
    }

    /**
     * Static helper that can type check if the value is a BinaryRep object.
     */
    static isBinary(value: unknown): value is BinaryRep {
        return (
            value !== null &&
            value !== undefined &&
            typeof value === "object" &&
            Object.prototype.hasOwnProperty.call(value, "binaryType")
        )
    }


    /**
     * Static helper that can type check if the value is a valid JSON representation of a BinaryRep object.
     */
    static isBinaryJSON(value: unknown): value is string {
        return typeof value === "string" && BinaryRep.isBinary(JSON.parse(value))
    }
}
